import { defineStore } from 'pinia'
import axios from 'axios'
import { ref } from 'vue'
import { logger } from '../utils/logger'
import { getApiUrl } from '../utils/api'

// Configure axios defaults
const baseURL = import.meta.env.DEV ? import.meta.env.VITE_BACKEND_URL : 'https://api.whisperherb.com'
axios.defaults.baseURL = baseURL

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    token: localStorage.getItem('token') || null,
    loading: false,
    error: null
  }),
  
  getters: {
    isAuthenticated: (state) => !!state.token,
    getUser: (state) => state.user,
    isLoading: (state) => state.loading,
    getError: (state) => state.error
  },
  
  actions: {
    clearError() {
      this.error = null;
    },
    
    setToken(token) {
      if (!token) {
        logger.warn('Attempted to set null or undefined token')
        return
      }
      
      logger.debug('Setting token in authStore')
      this.token = token
      localStorage.setItem('token', token)
      
      // Set the auth header only - don't add any custom headers that might trigger CORS preflight
      if (axios.defaults.headers.common) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
        logger.debug('Authorization header set in axios defaults')
      }
    },
    
    clearToken() {
      this.token = null
      localStorage.removeItem('token')
      delete axios.defaults.headers.common['Authorization']
    },
    
    setUser(userData) {
      logger.debug('Setting user data:', userData)
      this.user = {
        id: userData.id,
        name: userData.username.charAt(0).toUpperCase() + userData.username.slice(1), // Capitalize first character
        email: userData.email,
        created_at: userData.created_at
      }
    },
    
    async fetchUser() {
      try {
        logger.debug('Fetching user profile with token:', this.token ? 'Token exists' : 'No token')
        
        // Fetch profile with a custom config that's minimized to avoid CORS issues
        const response = await axios({
          method: 'get',
          url: '/api/auth/profile',
          headers: {
            'Authorization': `Bearer ${this.token}`
          },
          withCredentials: false,
          timeout: 8000
        })
        
        logger.debug('User profile data received')
        this.setUser(response.data)
        
        logger.debug('Updated user state:', this.user)
        return true
      } catch (error) {
        logger.error('Error fetching user profile:', error)
        
        if (error.response) {
          logger.error('Profile API response error:', {
            status: error.response.status,
            url: error.config?.url
          })
        } else {
          logger.error('Network or timeout error for profile request')
        }
        
        // If we get a 401 or 404, clear the auth
        if (error.response && (error.response.status === 401 || error.response.status === 404)) {
          logger.warn(`Auth error: ${error.response.status} response from profile endpoint`)
          this.clearAuth()
        }
        return false
      }
    },
    
    async login(credentials) {
      this.loading = true
      this.error = null
      
      try {
        const params = new URLSearchParams()
        params.append('username', credentials.username)
        params.append('password', credentials.password)
        
        // Try getting the token from the authentication endpoint
        logger.debug('Attempting login for user:', credentials.username)
        
        // Set a timeout for the request
        const timeoutId = setTimeout(() => {
          this.loading = false
          this.error = 'Login request timed out. Please try again.'
          logger.error('Login request timed out')
        }, 10000) // 10 second timeout
        
        // Make login request with correct content type for OAuth2 form
        const response = await axios({
          method: 'post',
          url: '/api/auth/login',
          data: params,
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          withCredentials: false,
          timeout: 8000
        })
        
        // Clear the timeout
        clearTimeout(timeoutId)
        
        logger.debug('Login response received')
        
        // Store token
        if (response.data?.access_token) {
          const token = response.data.access_token
          this.setToken(token)
          
          // Get user profile
          const profileSuccess = await this.fetchUser()
          if (!profileSuccess) {
            logger.warn('Got token but failed to fetch user profile')
          }
        } else {
          logger.error('Login response missing access_token:', response.data)
          throw new Error('Invalid login response format')
        }
        
        this.loading = false
        return { success: true }
      } catch (error) {
        // Always ensure loading state is reset regardless of error type
        this.loading = false
        
        // Handle network errors specifically
        if (error.message === 'Network Error') {
          this.error = 'Network error. Please check your connection and try again.'
          logger.error('Login network error:', error)
          return { success: false, error: this.error }
        }
        
        const errorMsg = error.response?.data?.detail || 'Failed to login. Please try again.'
        this.error = errorMsg
        
        logger.error('Login error:', errorMsg)
        logger.error('API endpoint issue:', error.config?.url, error.response?.status)
        return { success: false, error: errorMsg }
      }
    },
    
    async register(userData) {
      this.loading = true
      this.error = null
      
      try {
        logger.debug('Registering user with data:', { ...userData, password: '[REDACTED]' })
        
        // Make register request with appropriate CORS settings
        const response = await axios.post('/api/auth/register', userData, {
          withCredentials: false
        })
        
        logger.debug('Registration response:', response.data)
        
        // Auto login after successful registration
        logger.debug('Registration successful, attempting automatic login')
        await this.login({ 
          username: userData.username || userData.email, // Support either username or email for login
          password: userData.password
        })
        
        this.loading = false
        return { success: true }
      } catch (error) {
        this.loading = false
        
        const errorMsg = error.response?.data?.detail || 'Registration failed. Please try again.'
        this.error = errorMsg
        
        logger.error('Registration error:', errorMsg, error)
        logger.error('API endpoint issue:', error.config?.url, error.response?.status)
        return { success: false, error: errorMsg }
      }
    },
    
    logout() {
      this.clearAuth()
    },
    
    clearAuth() {
      this.clearToken()
      this.user = null
    },
    
    async initAuth() {
      logger.debug('Initializing authentication')
      
      logger.debug('Checking persisted state:', this.user ? 'User exists in store' : 'No user in store')
      
      // Check if we have a token
      if (this.token) {
        logger.debug('Found token in localStorage, initializing auth')
        
        try {
          // Fetch current user data
          const success = await this.fetchUser()
          
          logger.debug('User fetched successfully:', this.user ? 'User loaded' : 'No user data')
          
          return success
        } catch (error) {
          logger.error('Error initializing auth:', error)
          this.clearAuth()
          return false
        }
      } else if (this.user) {
        logger.debug('Using persisted user data:', this.user.name)
        // We have a user in the store but no token - this is inconsistent
        // Clear the auth state to be safe
        this.clearAuth()
      } else {
        logger.debug('No token found during initialization')
      }
      
      return false
    }
  },
  
  persist: {
    enabled: true,
    strategies: [
      {
        key: 'auth',
        storage: localStorage,
        paths: ['user']
      }
    ]
  }
}) 